import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { OrderInitType, orderInitState } from './state';
import { dateFormatter } from 'src/utils/dateFormatter';
import { AppState } from 'src/store';
import { BASE_URL } from 'src/config/api';
import client from 'src/api/client';
import { OrderItem } from 'src/admin-entity/interface/order/order-item.interface';
import { OrderItemStatus } from 'src/admin-entity/enum/order/order-item-status.enum';

export const getOrderSearchURL = (state: OrderInitType, baseUrl: string): string => {
    const {
        allSearchName,
        allSearchPhone,
        distributorName,
        endDate,
        orderItemProductStatus,
        paymentMethodType,
        productName,
        blacklistGrade,
        searchToken,
        searchTopic,
        startDate,
        take,
        page,
    } = state;

    const url = new URL(baseUrl);
    url.searchParams.append('startDate', dateFormatter(new Date(startDate), 'YYYY-MM-DD'));
    url.searchParams.append('endDate', dateFormatter(new Date(endDate), 'YYYY-MM-DD'));
    url.searchParams.append('page', `${page}`);
    url.searchParams.append('take', `${take}`);

    if (searchToken) {
        url.searchParams.append('searchTopic', searchTopic);
        url.searchParams.append(`${searchTopic}`, searchToken);
    }
    if (productName) {
        url.searchParams.append('productName', productName);
    }
    if (allSearchPhone) {
        url.searchParams.append('allSearchPhone', allSearchPhone);
    }
    if (allSearchName) {
        url.searchParams.append('allSearchName', allSearchName);
    }
    if (distributorName) {
        url.searchParams.append('distributorName', distributorName);
    }
    if (blacklistGrade !== '') {
        url.searchParams.append('blacklistGrade', blacklistGrade);
    }
    if (paymentMethodType.length > 0) {
        url.searchParams.append(`paymentMethodType`, paymentMethodType.join('|'));
    }
    if (orderItemProductStatus.length > 0) {
        url.searchParams.append('orderItemProductStatus', orderItemProductStatus.join('|'));
    }

    return url.toString();
};

export const searchOrderItem = createAsyncThunk(
    `orderSlice, searchOrderItem`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = (getState() as AppState).order;
            const url = getOrderSearchURL(state, `${BASE_URL}/v1/admin/order-items`);
            console.log(url);
            const res = await client.get(url.toString());
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const orderSlice = createSlice({
    name: 'orderSlice',
    initialState: orderInitState,
    reducers: {
        init: state => {
            return { ...orderInitState };
        },
        reset: state => {
            return {
                ...orderInitState,
                searchResult: state.searchResult,
            };
        },
        searchUrl: state => {
            const url = getOrderSearchURL(state, `${window.location.origin}/order/list`);
            window.history.pushState({}, '', url);
            window.dispatchEvent(new PopStateEvent('popstate'));
        },
        setSearchValues: (state, action: PayloadAction<string>) => {
            const params = new URLSearchParams(action.payload);
            state.startDate = params.get('startDate');
            state.endDate = params.get('endDate');
            state.page = +params.get('page');
            state.take = +params.get('take');

            if (params.get('searchTopic')) {
                state.searchTopic = params.get('searchTopic');
                state.searchToken = params.get(params.get('searchTopic'));
            }
            if (params.get('productName')) {
                state.productName = params.get('productName');
            }
            if (params.get(`allSearchPhone`)) {
                state.allSearchPhone = params.get('allSearchPhone');
            }
            if (params.get(`allSearchName`)) {
                state.allSearchName = params.get('allSearchName');
            }
            if (params.get(`distributorName`)) {
                state.distributorName = params.get('distributorName');
            }
            if (params.get(`blacklistGrade`)) {
                state.blacklistGrade = params.get('blacklistGrade');
            }
            if (params.get(`paymentMethodType`)) {
                console.log(params.get('paymentMethodType').split('|'));
                state.paymentMethodType = params.get('paymentMethodType').split('|');
            } else {
                console.log('dddd');
                state.paymentMethodType = [];
            }
            if (params.get(`orderItemProductStatus`)) {
                state.orderItemProductStatus = params.get('orderItemProductStatus').split('|');
            } else {
                state.orderItemProductStatus = [];
            }
        },
        updatePage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        updateTake: (state, action: PayloadAction<number>) => {
            state.take = action.payload;
        },
        updateSearchTopic: (state, action: PayloadAction<string>) => {
            state.searchTopic = action.payload;
        },
        updateSearchToken: (state, action: PayloadAction<string>) => {
            state.searchToken = action.payload;
        },
        updateProductName: (state, action: PayloadAction<string>) => {
            state.productName = action.payload;
        },
        updateAllSearchName: (state, action: PayloadAction<string>) => {
            state.allSearchName = action.payload;
        },
        updateAllSearchPhone: (state, action: PayloadAction<string>) => {
            state.allSearchPhone = action.payload;
        },
        updateDistributorName: (state, action: PayloadAction<string>) => {
            state.distributorName = action.payload;
        },
        updateSearchBlacklistGrade: (state, action: PayloadAction<string>) => {
            state.blacklistGrade = action.payload;
        },
        updateStartDate: (state, action: PayloadAction<string>) => {
            state.startDate = action.payload;
        },
        updateEndDate: (state, action: PayloadAction<string>) => {
            state.endDate = action.payload;
        },
        updatePaymentMethodType: (state, action: PayloadAction<string[]>) => {
            state.paymentMethodType = action.payload;
        },
        addPaymentMethodType: (state, action: PayloadAction<string>) => {
            state.paymentMethodType.push(action.payload);
        },
        removePaymentMethodType: (state, action: PayloadAction<string>) => {
            state.paymentMethodType = state.paymentMethodType.filter(value => value !== action.payload);
        },
        updateOrderItemProductStatus: (state, action: PayloadAction<string[]>) => {
            state.orderItemProductStatus = action.payload;
        },
        addOrderItemProductStatus: (state, action: PayloadAction<string>) => {
            state.orderItemProductStatus.push(action.payload);
        },
        removeOrderItemProductStatus: (state, action: PayloadAction<string>) => {
            state.orderItemProductStatus = state.orderItemProductStatus.filter(value => value !== action.payload);
        },
        updateOrderItemPage: (
            state,
            action: PayloadAction<{
                page: {
                    count: number;
                };
                orderItems: OrderItem[];
            }>,
        ) => {
            state.searchResult = action.payload;
        },
        updateIsWithdrawModalOpen: (state, action: PayloadAction<boolean>) => {
            state.isWithdrawModalOpen = action.payload;
        },
        updateIsWithdrawModalOpenOld: (state, action: PayloadAction<boolean>) => {
            state.isWithdrawModalOpenOld = action.payload;
        },
        setWithdrawOrderItem: (state, action: PayloadAction<OrderItem>) => {
            state.withdrawOrderItem = action.payload;
        },
        withdrawCompleteWithdrawOrderItem: state => {
            state.withdrawOrderItem.orderItemStatus = OrderItemStatus.WITHDRAW_COMPLETE;
        },
    },
    extraReducers: builder => {
        builder.addCase(searchOrderItem.fulfilled, (state, { payload }) => {
            state.searchResult = payload;
        });
    },
});

export const {
    init,
    reset,
    searchUrl,
    setSearchValues,
    updatePage,
    updateTake,
    updateSearchTopic,
    updateSearchToken,
    updateProductName,
    updateAllSearchName,
    updateAllSearchPhone,
    updateDistributorName,
    updateSearchBlacklistGrade,
    updateStartDate,
    updateEndDate,
    updatePaymentMethodType,
    addPaymentMethodType,
    removePaymentMethodType,
    addOrderItemProductStatus,
    removeOrderItemProductStatus,
    updateOrderItemProductStatus,
    updateOrderItemPage,
    setWithdrawOrderItem,
    updateIsWithdrawModalOpen,
    updateIsWithdrawModalOpenOld,
    withdrawCompleteWithdrawOrderItem,
} = orderSlice.actions;
export default orderSlice.reducer;
